import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { CaretDown, X } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useReactFlow } from 'reactflow';
import { getNodeExecutionOutput } from '../../api/getNodeExecutions';
import { nodeExecutions } from '../../queries/nodeExecutions';
import { sumNodeExecutionSpend } from '../../util/nodeExecutionSpend';

interface Props {
  open: boolean;
  onClose: () => void;
  flowId: string;
  nodeId: string;
  nodeLabel?: string;
}

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function ExecutionDetails({ outputId }: { outputId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['nodeExecutionOutput', outputId],
    queryFn: () => getNodeExecutionOutput(outputId),
    staleTime: Infinity,
  });

  if (isLoading) {
    return <CircularProgress size={18} />;
  }

  if (isError || !data) {
    return (
      <Typography variant="body2" color="error">
        Failed to load execution detail.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Input
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            bgcolor: 'action.hover',
            borderRadius: 1,
            fontSize: 12,
            maxHeight: 200,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {JSON.stringify(data.input ?? {}, null, 2)}
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          Output
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 1,
            bgcolor: 'action.hover',
            borderRadius: 1,
            fontSize: 12,
            maxHeight: 280,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {data.output ?? JSON.stringify(data.outputData ?? {}, null, 2)}
        </Box>
      </Box>
    </Box>
  );
}

function NodeExecutionsModal({
  open,
  onClose,
  flowId,
  nodeId,
  nodeLabel,
}: Props) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const { setNodes } = useReactFlow();
  const { data, isLoading, isError } = useQuery({
    ...nodeExecutions.list(flowId, nodeId),
    enabled: open && !!flowId && !!nodeId,
  });
  const spend = data ? sumNodeExecutionSpend(data) : null;

  // Debug bridge (n8n-style): load a past run's output into the editor node as
  // an ephemeral preview — written to node.data via setNodes, NOT persisted as
  // definition (no autosave is triggered here).
  async function loadIntoEditor(outputId: string) {
    setLoadingId(outputId);
    try {
      const full = await getNodeExecutionOutput(outputId);
      const value =
        full.output ??
        (full.outputData ? JSON.stringify(full.outputData, null, 2) : '');
      setNodes(nds =>
        nds.map(n =>
          n.id === nodeId ? { ...n, data: { ...n.data, text: value } } : n,
        ),
      );
      onClose();
    } finally {
      setLoadingId(null);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <span>Executions{nodeLabel ? ` — ${nodeLabel}` : ''}</span>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {isError && (
          <Typography color="error">Failed to load executions.</Typography>
        )}
        {!isLoading && !isError && (!data || data.length === 0) && (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No executions yet.
          </Typography>
        )}
        {!isLoading && !isError && spend && spend.runCount > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${spend.totalTokens} tokens · ${spend.runCount} runs`}
            />
            <Typography variant="caption" color="text.secondary">
              Total spend (from execution history)
            </Typography>
          </Box>
        )}
        {!isLoading &&
          !isError &&
          data?.map(item => (
            <Accordion
              key={item.id}
              expanded={expandedId === item.id}
              onChange={(_, isExpanded) =>
                setExpandedId(isExpanded ? item.id : null)
              }
              disableGutters
            >
              <AccordionSummary expandIcon={<CaretDown size={16} />}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    width: '100%',
                  }}
                >
                  <Chip
                    size="small"
                    label={item.isError ? 'Failed' : 'Completed'}
                    color={item.isError ? 'error' : 'success'}
                    variant="outlined"
                  />
                  <Typography variant="body2">
                    {formatDate(item.createdAt)}
                  </Typography>
                  {item.userName && (
                    <Typography variant="caption" color="text.secondary">
                      {item.userName}
                    </Typography>
                  )}
                  {item.flowVersionHistoryId && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`v ${item.flowVersionHistoryId.slice(0, 8)}`}
                    />
                  )}
                  {typeof item.usedTokens === 'number' && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${Math.round(item.usedTokens * 1000) / 1000} tok`}
                    />
                  )}
                  {item.outputPreview && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        ml: 'auto',
                        maxWidth: 320,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.outputPreview}
                    </Typography>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {item.isError ? (
                  <Typography variant="body2" color="error">
                    {item.userMessage || item.message}
                  </Typography>
                ) : item.outputId ? (
                  expandedId === item.id && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <ExecutionDetails outputId={item.outputId} />
                      <Box>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={loadingId === item.outputId}
                          onClick={() => loadIntoEditor(item.outputId!)}
                        >
                          {loadingId === item.outputId
                            ? 'Loading…'
                            : 'Load into editor'}
                        </Button>
                      </Box>
                    </Box>
                  )
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No output recorded for this run.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
      </DialogContent>
    </Dialog>
  );
}

export default NodeExecutionsModal;
